/** @jsxRuntime classic */
/** @jsx jsx */

import { ReactNode, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { useAnimationsSectionContext } from './Context';
import { ContextInterface } from './types';

type Props = {
  children: ReactNode;
  onVisible?: () => void;
  rootMargin?: string;
};

export const ScrollFade = ({
  children,
  rootMargin = '0px',
  onVisible,
}: Props) => {
  const { width } = useAnimationsSectionContext();
  const elementRef = useRef<HTMLDivElement>(null);
  const prevWidth = useRef<ContextInterface['width']>();
  const prevRootMargin = useRef('');
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const ref = elementRef.current;
    const observer = new IntersectionObserver(
      ([{ isIntersecting, intersectionRatio }]) => {
        if (
          ref &&
          (width !== prevWidth.current || rootMargin !== prevRootMargin.current)
        )
          observer.unobserve(ref);
        if (isIntersecting && intersectionRatio >= 0.75) {
          setOpacity(1);
          if (onVisible) onVisible();
        } else if (isIntersecting) {
          setOpacity(intersectionRatio);
        } else if (intersectionRatio <= 0.2) {
          setOpacity(0);
        }
      },
      {
        threshold: Array.from({ length: 5 }).map((_, index) => index / 4),
        rootMargin,
      },
    );
    prevWidth.current = width;
    prevRootMargin.current = rootMargin;

    if (ref) {
      observer.observe(ref);
    }
    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [width, rootMargin, onVisible]);

  return (
    <div
      css={css({
        opacity,
        transition: 'opacity 0.25s ease, transform 0.25s ease',
        '@keyframes wipe-enter': {
          '0%': {
            transform: 'scale(0, .025)',
          },
          '50%': {
            transform: 'scale(1, .025)',
          },
        },
      })}
      ref={elementRef}
    >
      {children}
    </div>
  );
};
